/* ==========================================================================
   cq-pages.js v1.0.0 (2026-06-09)
   Crystal Quest Page Factory shared behavior layer.
   Same architecture as cq-education.js: ONE versioned CDN asset loaded by
   every Page Factory page; update once, every page improves.
   Deploy: upload to theme assets/, load in sections via
     <script src="{{ 'cq-pages.js' | asset_url }}" defer></script>
   Vanilla JS. No dependencies. Progressive enhancement only: every
   feature is additive; pages remain fully usable with JS disabled.

   Markup contracts:
   1. FAQ accordion
        <div class="cq-faq" data-cq-faq>
          <article class="cq-faq__item">
            <button class="cq-faq__question" type="button"
                    aria-expanded="false" aria-controls="ID">
              <h3 class="cq-faq__question-text">Q</h3>
              <span class="cq-faq__icon" aria-hidden="true">+</span>
            </button>
            <div class="cq-faq__answer" id="ID">
              <div class="cq-faq__answer-text">A</div>
            </div>
          </article>
        </div>
      (Legacy div[role=button] questions from the PFAS build also work.)
   2. Smooth scroll: any in-page <a href="#...">. Respects reduced motion.
   3. Lazy embeds: <iframe data-cq-src="..."> inside .cq-embed; src is set
      when the embed approaches the viewport.
   4. Reveals: elements with .cq-reveal get .is-visible in view.

   Changelog at: 50-Staging/page-builders/assets/_ASSET-LAYER.md
   ========================================================================== */
(function () {
  'use strict';
  var d = document;
  d.documentElement.classList.add('cq-js');

  /* ---------------- 1. FAQ accordion (one-at-a-time, ARIA, keyboard) ---- */
  function initFaq(root) {
    if (root.getAttribute('data-cq-faq-ready')) return;
    root.setAttribute('data-cq-faq-ready', '1');
    root.classList.add('cq-faq--enhanced');

    var items = root.querySelectorAll('.cq-faq__item');

    function setOpen(item, open) {
      var q = item.querySelector('.cq-faq__question');
      item.classList.toggle('is-open', open);
      if (q) q.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function toggle(item) {
      var willOpen = !item.classList.contains('is-open');
      for (var i = 0; i < items.length; i++) setOpen(items[i], false);
      if (willOpen) setOpen(item, true);
    }

    root.addEventListener('click', function (e) {
      var q = e.target.closest('.cq-faq__question');
      if (!q || !root.contains(q)) return;
      var item = q.closest('.cq-faq__item');
      if (item) toggle(item);
    });

    /* Keyboard support for legacy non-button questions (PFAS pattern used
       div[role=button][tabindex=0]); real <button> handles this natively. */
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var q = e.target.closest('.cq-faq__question');
      if (!q || q.tagName === 'BUTTON') return;
      e.preventDefault();
      var item = q.closest('.cq-faq__item');
      if (item) toggle(item);
    });

    for (var i = 0; i < items.length; i++) setOpen(items[i], false);
  }

  /* ---------------- 2. Smooth in-page scroll ---------------------------- */
  function initSmoothScroll() {
    var reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    d.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      var target = d.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({
        behavior: reduced ? 'auto' : 'smooth',
        block: 'start'
      });
      /* keep keyboard/AT context with the scroll */
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      if (history.replaceState) history.replaceState(null, '', '#' + id);
    });
  }

  /* ---------------- 3. Lazy embeds (iframes, heavy widgets) ------------- */
  function initLazyEmbeds() {
    var frames = d.querySelectorAll('iframe[data-cq-src]');
    if (!frames.length) return;

    function load(frame) {
      if (!frame.getAttribute('src')) {
        frame.setAttribute('src', frame.getAttribute('data-cq-src'));
        frame.removeAttribute('data-cq-src');
      }
    }

    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < frames.length; i++) load(frames[i]);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          load(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '300px 0px' });
    for (var j = 0; j < frames.length; j++) io.observe(frames[j]);
  }

  /* ---------------- 4. Scroll reveals ----------------------------------- */
  function initReveals() {
    var els = d.querySelectorAll('.cq-reveal');
    if (!els.length) return;
    var reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      for (var i = 0; i < els.length; i++) els[i].classList.add('is-visible');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    for (var j = 0; j < els.length; j++) io.observe(els[j]);
  }

  /* ---------------- boot ------------------------------------------------ */
  function init() {
    var faqs = d.querySelectorAll('[data-cq-faq], .cq-faq');
    for (var i = 0; i < faqs.length; i++) initFaq(faqs[i]);
    initSmoothScroll();
    initLazyEmbeds();
    initReveals();
  }

  if (d.readyState === 'loading') {
    d.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
